import { useEffect, useState } from "react";
import { Button, FormField } from "@/design-system/components/ui";
import { Textarea } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import type { DealDetailDto, DealListItemDto } from "@/types/deals";
import { useArchiveDeal, useRestoreDeal } from "@/hooks/deals/useDeals";
import { dealLabel } from "./dealFormat";

interface BaseProps {
  open: boolean;
  deal: DealListItemDto | DealDetailDto | null;
  onClose: () => void;
}

interface ArchiveProps extends BaseProps {
  onArchived?: (deal: DealDetailDto) => void;
}

/** Spec §5.4 — Archive confirmation. Restricted by Deals.Archive at the
 *  call site. Reason is optional per the API contract. */
export function ArchiveConfirmModal({ open, deal, onClose, onArchived }: ArchiveProps) {
  const toast = useToast();
  const archive = useArchiveDeal(deal?.id ?? "");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReason("");
      setError(null);
    }
  }, [open]);

  if (!deal) return null;

  async function submit() {
    setError(null);
    try {
      const result = await archive.mutateAsync({ reason: reason.trim() || null });
      toast.success(`Archived ${dealLabel(deal!)}.`);
      onArchived?.(result);
      onClose();
    } catch (err) {
      const msg = err instanceof ApiError && err.errors.length
        ? err.errors.join("; ")
        : err instanceof Error
          ? err.message
          : "Archive failed.";
      setError(msg);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Archive deal"
      description={`${deal.dealNumber} — ${dealLabel(deal)}`}
      footer={
        <>
          <Button variant="tertiary" onClick={onClose} disabled={archive.isPending}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={submit} loading={archive.isPending}>
            Archive
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-3">
        <p className="text-small text-onyx">
          The deal moves out of the active pipeline and into{" "}
          <strong>/pipeline/archive</strong>. Records, audit trail, and linked
          counterparties stay intact; only Kanban / dashboard visibility changes.
          E1+ / ADMIN can restore it later.
        </p>
        <FormField label="Reason (optional)">
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Optional context — appears on the activity feed."
          />
        </FormField>
        {error && (
          <p className="text-small text-status-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}

interface RestoreProps extends BaseProps {
  onRestored?: (deal: DealDetailDto) => void;
}

/** Spec §5.4 — Restore an archived deal. E1+ / ADMIN only. */
export function RestoreModal({ open, deal, onClose, onRestored }: RestoreProps) {
  const toast = useToast();
  const restore = useRestoreDeal(deal?.id ?? "");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  if (!deal) return null;

  async function submit() {
    setError(null);
    try {
      const result = await restore.mutateAsync();
      toast.success(`Restored ${dealLabel(deal!)}.`);
      onRestored?.(result);
      onClose();
    } catch (err) {
      const msg = err instanceof ApiError && err.errors.length
        ? err.errors.join("; ")
        : err instanceof Error
          ? err.message
          : "Restore failed.";
      setError(msg);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Restore deal"
      description={`${deal.dealNumber} — ${dealLabel(deal)}`}
      footer={
        <>
          <Button variant="tertiary" onClick={onClose} disabled={restore.isPending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} loading={restore.isPending}>
            Restore
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-3">
        <p className="text-small text-onyx">
          The deal returns to the active pipeline. The activity feed records
          the restore. Re-opening a Declined / Withdrawn deal does not
          reset its terminal reason; if needed, edit those fields from the
          Deal Detail page after restore.
        </p>
        {error && (
          <p className="text-small text-status-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
